import { inject, InjectionToken, Service, signal } from '@angular/core';

/**
 * The user-facing theme modes. `system` means the user has made no explicit choice and the
 * effective theme follows the operating system's `prefers-color-scheme`.
 */
export type ThemeMode = 'system' | 'light' | 'dark';

/**
 * Opt-in flag for dark mode. Only apps that actually render the dark theme (currently the internal
 * portals, portal-guide and portal-admin) provide this as `true`. Without it ThemeModeService stays
 * inert and `isDark()` stays false, so dark mode never leaks into apps that only ship the light
 * theme — even when the service is pulled in transitively (e.g. by the shared code editor). It also
 * decides whether mc-theme-mode-toggle renders its button.
 */
export const DARK_MODE_ENABLED = new InjectionToken<boolean>('DARK_MODE_ENABLED');

/**
 * Resolves, applies, and persists the portal's light/dark theme.
 *
 * The effective theme comes from the stored preference, falling back to the OS
 * `prefers-color-scheme` when the user has not chosen one. The matching Material theme class is
 * applied to the document root; a pre-boot script in each app's `index.html` applies the same class
 * before Angular loads so the first paint already matches (avoiding a flash of the wrong theme).
 */
@Service()
export class ThemeModeService {
  /**
   * localStorage key holding the persisted preference. Shared across all portals so a choice made
   * in one carries over as other apps adopt dark mode. Kept in sync with the pre-boot script.
   */
  static readonly StorageKey = 'mc-theme-preference';

  /** Root-element class that renders the light Material theme. */
  static readonly LightThemeClass = 'mc-peacock-theme';

  /** Root-element class that renders the dark Material theme. */
  static readonly DarkThemeClass = 'mc-peacock-dark-theme';

  /** Whether the dark theme is currently in effect. Reactive so navbar controls stay in sync. */
  readonly isDark = signal(false);

  private readonly systemDarkQuery = window.matchMedia('(prefers-color-scheme: dark)');

  /** Whether this app opted into dark mode. When false the service stays inert (see DARK_MODE_ENABLED). */
  readonly enabled = inject(DARK_MODE_ENABLED, { optional: true }) ?? false;

  constructor() {
    // Dark mode is opt-in per app; in apps that only ship the light theme, stay inert and leave
    // isDark() false so the dark class is never applied. The service can be constructed transitively
    // (e.g. the shared code editor injects it to read isDark()), so this guard is what keeps dark
    // mode from leaking into those apps.
    if (!this.enabled) {
      return;
    }

    // While the user is following the system preference, react live to OS light/dark changes.
    this.systemDarkQuery.addEventListener('change', () => {
      if (this.getMode() === 'system') {
        this.applyTheme();
      }
    });

    this.applyTheme();
  }

  /**
   * Returns the persisted mode, or `system` when the user has not made an explicit choice.
   */
  getMode(): ThemeMode {
    try {
      const stored = localStorage.getItem(ThemeModeService.StorageKey);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // localStorage can be unavailable (private mode / disabled storage); treat as system.
    }

    return 'system';
  }

  /**
   * Persists an explicit light or dark choice and applies it immediately.
   */
  setMode(mode: 'light' | 'dark') {
    if (!this.enabled) {
      return;
    }

    try {
      localStorage.setItem(ThemeModeService.StorageKey, mode);
    } catch {
      // Persisting is best-effort; still apply the theme for the current session.
    }

    this.applyTheme();
  }

  /**
   * Flips between light and dark based on the currently-effective theme, persisting the result.
   * There is no path back to `system` from here by design — an explicit choice sticks.
   */
  toggle() {
    this.setMode(this.isDark() ? 'light' : 'dark');
  }

  /**
   * Applies the effective theme's class and `color-scheme` to the document root and updates
   * {@link isDark}.
   */
  private applyTheme() {
    const dark = this.resolveIsDark();
    const root = document.documentElement;

    root.classList.toggle(ThemeModeService.DarkThemeClass, dark);
    root.classList.toggle(ThemeModeService.LightThemeClass, !dark);
    root.style.colorScheme = dark ? 'dark' : 'light';

    // The pre-boot script in index.html paints the <html> background inline (before Angular loads,
    // to avoid a flash). That inline style would otherwise stay fixed at the initial mode, so keep
    // it in sync with the now-themed body background whenever the mode changes.
    root.style.backgroundColor = getComputedStyle(document.body).backgroundColor;

    this.isDark.set(dark);
  }

  /**
   * Resolves the effective dark/light state, evaluating `system` against the OS preference.
   */
  private resolveIsDark(): boolean {
    const mode = this.getMode();
    return mode === 'system' ? this.systemDarkQuery.matches : mode === 'dark';
  }
}
